import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider, ProtectedRoute } from "@/lib/auth";
import { SettingsProvider } from "@/lib/settings";
import { CartProvider } from "@/lib/cart";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { AnnouncementBar } from "@/components/site/AnnouncementBar";
import { CartDrawer } from "@/components/site/CartDrawer";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import SpiritStore from "@/pages/SpiritStore";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Services from "@/pages/Services";
import Gallery from "@/pages/Gallery";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Quote from "@/pages/Quote";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const SiteLayout = ({ children }) => (
  <>
    <div className="fixed top-0 left-0 right-0 z-50">
      <AnnouncementBar />
      <Navbar />
    </div>
    <main>{children}</main>
    <Footer />
    <CartDrawer />
  </>
);

function App() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SettingsProvider>
            <CartProvider>
              <ScrollToTop />
              <Toaster position="bottom-right" richColors offset={24} />
              <Routes>
                {/* Admin (no marketing navbar/footer) */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/quotes" element={<Navigate to="/admin" replace />} />

                {/* Public site */}
                <Route path="/" element={<SiteLayout><Home /></SiteLayout>} />
                <Route path="/shop" element={<SiteLayout><Shop /></SiteLayout>} />
                <Route path="/spirit-store" element={<SiteLayout><SpiritStore /></SiteLayout>} />
                <Route path="/payment/success" element={<SiteLayout><PaymentSuccess /></SiteLayout>} />
                <Route path="/services" element={<SiteLayout><Services /></SiteLayout>} />
                <Route path="/gallery" element={<SiteLayout><Gallery /></SiteLayout>} />
                <Route path="/about" element={<SiteLayout><About /></SiteLayout>} />
                <Route path="/contact" element={<SiteLayout><Contact /></SiteLayout>} />
                <Route path="/quote" element={<SiteLayout><Quote /></SiteLayout>} />
              </Routes>
            </CartProvider>
          </SettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
